import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  orgCustomFieldsService,
  OrgCustomField,
  OrgCustomFieldEntityType,
} from '../services/orgCustomFields'
import type { ApiErrorLike } from '../utils/apiError'
import { useCurrentOrgId } from '../contexts/AuthContext'

interface CustomFieldsPanelProps {
  entityType: OrgCustomFieldEntityType
  entityId: number | null | undefined
  organizationId?: number
  /** When entityId is null (e.g. on a create form), the panel only collects values
   *  in local state and surfaces them via `onPendingChange` so the parent can submit
   *  them after the entity is created. */
  onPendingChange?: (values: Record<string, unknown>) => void
  className?: string
}

export function CustomFieldsPanel({
  entityType,
  entityId,
  organizationId: organizationIdProp,
  onPendingChange,
  className = '',
}: CustomFieldsPanelProps) {
  const currentOrgId = useCurrentOrgId()
  const organizationId = organizationIdProp ?? currentOrgId ?? 1
  const [fields, setFields] = useState<OrgCustomField[]>([])
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    orgCustomFieldsService
      .list(organizationId, entityType)
      .then(catalog => {
        if (cancelled) return
        setFields(catalog)
        if (entityId == null) {
          setLoading(false)
          return null
        }
        return orgCustomFieldsService.getValues(entityType, entityId, organizationId)
      })
      .then(resp => {
        if (cancelled || !resp) return
        setValues(resp.values ?? {})
      })
      .catch(() => {
        if (!cancelled) setFields([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [organizationId, entityType, entityId])

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => a.display_order - b.display_order || a.id - b.id),
    [fields],
  )

  function update(key: string, value: unknown) {
    setValues(prev => {
      const next = { ...prev, [key]: value }
      onPendingChange?.(next)
      return next
    })
  }

  async function save() {
    if (entityId == null) return
    setSaving(true)
    try {
      const resp = await orgCustomFieldsService.setValues(entityType, entityId, values, organizationId)
      setValues(resp.values ?? {})
      toast.success('Custom fields saved')
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err?.response?.data?.detail ?? 'Failed to save custom fields')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className={`text-sm text-muted-foreground ${className}`}>Loading custom fields…</div>
  }

  if (sortedFields.length === 0) {
    return null
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <h3 className="text-base font-semibold text-foreground">Custom fields</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sortedFields.map(field => (
          <FieldRow
            key={field.id}
            field={field}
            value={values[field.key]}
            onChange={v => update(field.key, v)}
          />
        ))}
      </div>
      {entityId != null && !onPendingChange && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save custom fields'}
          </button>
        </div>
      )}
    </div>
  )
}

function FieldRow({
  field,
  value,
  onChange,
}: {
  field: OrgCustomField
  value: unknown
  onChange: (v: unknown) => void
}) {
  const id = `cf-${field.id}`
  const label = (
    <label htmlFor={id} className="text-xs font-medium text-foreground block mb-1">
      {field.label}
      {field.required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  )
  const help = field.help_text ? (
    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
  ) : null
  const baseInput =
    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  switch (field.field_type) {
    case 'longtext':
      return (
        <div>
          {label}
          <textarea
            id={id}
            value={(value as string) ?? ''}
            onChange={e => onChange(e.target.value)}
            rows={3}
            className={`${baseInput} resize-none`}
          />
          {help}
        </div>
      )
    case 'number':
    case 'currency':
      return (
        <div>
          {label}
          <input
            id={id}
            type="number"
            value={value == null ? '' : String(value)}
            onChange={e => onChange(e.target.value)}
            className={baseInput}
          />
          {help}
        </div>
      )
    case 'date':
      return (
        <div>
          {label}
          <input
            id={id}
            type="date"
            value={(value as string) ?? ''}
            onChange={e => onChange(e.target.value)}
            className={baseInput}
          />
          {help}
        </div>
      )
    case 'select':
      return (
        <div>
          {label}
          <select
            id={id}
            value={(value as string) ?? ''}
            onChange={e => onChange(e.target.value || null)}
            className={baseInput}
          >
            <option value="">—</option>
            {(field.options ?? []).map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {help}
        </div>
      )
    case 'multiselect': {
      const current = Array.isArray(value) ? (value as string[]) : []
      return (
        <div>
          {label}
          <div className="flex flex-wrap gap-2">
            {(field.options ?? []).map(opt => {
              const selected = current.includes(opt.value)
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() =>
                    onChange(selected ? current.filter(v => v !== opt.value) : [...current, opt.value])
                  }
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    selected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-input hover:bg-muted/30'
                  }`}
                  aria-pressed={selected}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
          {help}
        </div>
      )
    }
    case 'url':
      return (
        <div>
          {label}
          <input
            id={id}
            type="url"
            value={(value as string) ?? ''}
            onChange={e => onChange(e.target.value)}
            placeholder="https://…"
            className={baseInput}
          />
          {help}
        </div>
      )
    case 'user':
    case 'text':
    default:
      return (
        <div>
          {label}
          <input
            id={id}
            type="text"
            value={(value as string) ?? ''}
            onChange={e => onChange(e.target.value)}
            className={baseInput}
          />
          {help}
        </div>
      )
  }
}
